import { useCallback, useState } from 'react';
import { useOutletContext } from 'react-router';

import { useRfidCards } from '@/api/cards';
import type { RfidCardType } from '@/api/schema';
import { Table } from '@/components/Table/Table';
import { createDebugConfig, trackCustomEvent } from '@/configuration/setup/googleAnalytics';
import { columns } from '@/features/administration/cards/columns';
import { ActivationAlert } from '@/features/administration/cards/components/ActivationAlert';
import { Toolbar } from '@/features/administration/cards/components/Toolbar';
import { t } from '@/lib/utils';

type AdminContext = {
    onOpenSidebar: (card: RfidCardType) => void;
    isSidebarOpen: boolean;
};

const ChargingCards = () => {
    const [activeFilter, setActiveFilter] = useState<boolean | undefined>(undefined);
    const { onOpenSidebar, isSidebarOpen } = useOutletContext<AdminContext>();
    const { data: cards, isLoading } = useRfidCards();
    const debugConfig = createDebugConfig();

    const noResultsConfig = {
        title: t('chargingCards.noResultsFound.title'),
        description: t('chargingCards.noResultsFound.description'),
    };

    const filterCardsByStatus = useCallback(
        (row: RfidCardType) => activeFilter === undefined || row.isActive === activeFilter,
        [activeFilter]
    );

    const handleRowClick = (card: RfidCardType) => {
        trackCustomEvent(
            'charging_cards_select',
            { trigger: 'click', flow_name: 'charging_cards_management', element_name: 'card_row' },
            debugConfig
        );
        onOpenSidebar(card);
    };

    return (
        <>
            <ActivationAlert />
            <div className='padding-top-15'>
                <Table
                    data={cards?.cards ?? []}
                    columns={columns(t)}
                    isLoading={isLoading}
                    searchByLabel={t('general.searchBy')}
                    noMoreMessage={t('chargingCards.everythingIsLoaded')}
                    noResults={noResultsConfig}
                    nonFilterableColumns={['active']}
                    isSidebarOpen={isSidebarOpen}
                    openRowSidebar={handleRowClick}
                    showFilter
                    extraFilters={<Toolbar activeSelected={activeFilter} setActiveSelected={setActiveFilter} />}
                    extraFiltersFn={filterCardsByStatus}
                    initialSortKey='active'
                    getRowKey={row => row.serial}
                />
            </div>
        </>
    );
};

export default ChargingCards;
